import sharedSupabase from '../config/supabase';
import { findCrossReference } from '../utils/itemCrossReference';
import { isLiveInventoryItem } from '../utils/countItemLiveness';
import { upsertManualOption } from './purchaseOptionService';
import { requestComparisonRecompute } from './comparisonRecomputeService';
import { statedOrderUnitFromPack } from '../../../supabase/functions/_shared/packText.mjs';

// A merge/attach/detach changes what a relationship CONTAINS, so its price
// comparison must be re-resolved. Fire-and-forget: the RPC already succeeded.
async function recomputeAfterRelationshipChange(client, { storeId, itemIds = [], suggestionId = null }) {
  try {
    let store = storeId ?? null;
    const targets = new Set(itemIds.filter((id) => id != null));
    if (suggestionId != null && targets.size === 0) {
      const { data } = await client
        .from('item_cross_reference_suggestions')
        .select('store_id, primary_item_id')
        .eq('id', suggestionId)
        .maybeSingle();
      if (data?.primary_item_id != null) {
        targets.add(data.primary_item_id);
        store = store ?? data.store_id;
      }
    }
    for (const itemId of targets) {
      // eslint-disable-next-line no-await-in-loop
      await requestComparisonRecompute(store, itemId, client);
    }
  } catch {
    // Comparison freshness is a nicety; the relationship change stands.
  }
}

/**
 * Item Cross-Reference Service (web)
 *
 * Data access for the cross-reference flow: when a manually entered item looks
 * like the same PRODUCT as an existing item (different vendor and/or package
 * size — see utils/itemCrossReference), record a pending suggestion instead of
 * silently merging. Reviewers then accept (options move to the primary item,
 * duplicate is archived) or dismiss from the suggestions queue.
 *
 * The backing RPCs (attach_purchase_option_to_item,
 * detach_purchase_option_to_new_item, resolve_cross_reference_suggestion) and
 * tables (item_cross_reference_suggestions, item_cross_reference_events) ship
 * in a migration that may not be deployed everywhere yet — every call against
 * them degrades gracefully (console.warn + null/[]) when the schema is missing
 * instead of crashing the UI.
 *
 * Every function takes an optional trailing `client` (defaults to the shared
 * supabase singleton) so tests can inject a fake.
 */

const SUGGESTION_COLUMNS =
  'id, store_id, primary_item_id, duplicate_item_id, score, reason, channel, status, created_at';

const throwIfError = (error) => {
  if (error) throw new Error(error.message);
};

// PostgREST/Postgres "not deployed yet" signatures: function or table missing
// from the schema cache (PGRST202/PGRST205) or from the database itself
// (42883 undefined_function / 42P01 undefined_table).
const isMissingSchemaError = (error) => {
  const code = String(error?.code || '');
  const message = String(error?.message || '');
  return (
    ['PGRST202', 'PGRST205', '42883', '42P01'].includes(code) ||
    /could not find the (function|table)|schema cache|does not exist/i.test(message)
  );
};

const warnMissingSchema = (what, error) => {
  console.warn(`[item-cross-reference] ${what} unavailable (migration not deployed?):`, error?.message);
};

// jsonb RPC results arrive already parsed on PostgREST v10+ but as a string on
// older versions — accept both.
const parseJsonb = (data) => {
  if (data === null || data === undefined) return null;
  return typeof data === 'string' ? JSON.parse(data) : data;
};

const positiveNumber = (value) => {
  const parsed = typeof value === 'string' ? Number.parseFloat(value) : value;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

// Exact browser mirror of public.inventory_item_key_part() and the
// merge_inventory_item_duplicate_insert key. The BEFORE INSERT trigger can
// merge a row and RETURN NULL, so callers must resolve that survivor before
// creating the incoming vendor product. Keep this tiny identity boundary in
// lockstep with menu-starter/core.mjs#inventoryItemTriggerMergeKey.
export const inventoryItemKeyPart = (value) =>
  String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

export function inventoryItemTriggerMergeKey(source, { forInsert = false } = {}) {
  const row = source && typeof source === 'object' ? source : {};
  const unit = inventoryItemKeyPart(row.unit);
  return [
    inventoryItemKeyPart(row.description),
    forInsert ? (unit || 'each') : unit,
    inventoryItemKeyPart(row.vendor),
  ].join(' ');
}

/** Resolve the live row swallowed by merge_inventory_item_duplicate_insert. */
export async function findInventoryItemAfterDuplicateMerge(
  storeId,
  source,
  opts = {},
  client = sharedSupabase,
) {
  if (!storeId || !String(source?.description ?? '').trim()) return null;
  const wantedKey = inventoryItemTriggerMergeKey(source, { forInsert: true });
  const snapshotMatch = (Array.isArray(opts.catalog) ? opts.catalog : []).find(
    (candidate) => inventoryItemTriggerMergeKey(candidate) === wantedKey,
  );
  if (snapshotMatch?.id != null) return snapshotMatch;

  // SQL collapses whitespace. `%` between the source words admits the same
  // spacing variants; the exact JS key below rejects every broader ILIKE hit.
  const descriptionPattern = String(source.description).trim().split(/\s+/).join('%');
  const { data, error } = await client
    .from('inventory_items')
    .select('*')
    .eq('store_id', storeId)
    .or('is_active.eq.true,is_active.is.null')
    .ilike('description', descriptionPattern)
    .order('updated_at', { ascending: false })
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .limit(100);
  throwIfError(error);
  return (data || []).find(
    (candidate) => inventoryItemTriggerMergeKey(candidate) === wantedKey,
  ) || null;
}

/**
 * Match a batch INSERT's returned rows back to its sources without relying on
 * array position. Suppressed duplicates are resolved to their live survivor;
 * `inserted` is true only for rows PostgreSQL actually inserted.
 */
export async function resolveInventoryInsertResults(
  storeId,
  sourceRows,
  returnedRows,
  opts = {},
  client = sharedSupabase,
) {
  const sources = Array.isArray(sourceRows) ? sourceRows : [];
  const returned = Array.isArray(returnedRows) ? returnedRows : [];
  const returnedByKey = new Map();
  returned.forEach((row) => {
    const key = inventoryItemTriggerMergeKey(row);
    const queue = returnedByKey.get(key) || [];
    queue.push(row);
    returnedByKey.set(key, queue);
  });

  const catalog = [
    ...(Array.isArray(opts.catalog) ? opts.catalog : []),
    ...returned,
  ];
  const outcomes = [];
  for (const source of sources) {
    const key = inventoryItemTriggerMergeKey(source, { forInsert: true });
    const returnedQueue = returnedByKey.get(key) || [];
    const insertedRow = returnedQueue.shift() || null;
    if (insertedRow) {
      outcomes.push({ source, item: insertedRow, inserted: true });
      continue;
    }
    // eslint-disable-next-line no-await-in-loop
    const survivor = await findInventoryItemAfterDuplicateMerge(
      storeId,
      source,
      { catalog },
      client,
    );
    if (survivor?.id != null) {
      catalog.push(survivor);
      outcomes.push({ source, item: survivor, inserted: false });
    } else {
      outcomes.push({ source, item: null, inserted: false });
    }
  }
  return outcomes;
}

/**
 * Check-then-insert equivalent of ON CONFLICT DO NOTHING for
 * item_cross_reference_suggestions: a pending suggestion for the same pair —
 * in EITHER direction — is returned as-is, and a concurrent writer hitting
 * the unique index is tolerated quietly. The mirror check matters because
 * the unique index is on the ORDERED pair: a stored (B, A) does not conflict
 * with a new (A, B), and two pending mirrors double-merge when both are
 * accepted.
 *
 * A DISMISSED row — same pair or its mirror — is a human "keep separate",
 * and a matcher re-proposing the pair (in either direction) must not
 * relitigate it: the suggestion is suppressed (null). Only the unique index
 * used to catch the same-direction case, and the mirrored direction slipped
 * through entirely. `overrideDismissed` is for the item editor's EXPLICIT
 * combine, where a human is deliberately reversing the earlier decision.
 */
async function insertSuggestion(storeId, row, client, { overrideDismissed = false } = {}) {
  const pair = `and(primary_item_id.eq.${row.primary_item_id},duplicate_item_id.eq.${row.duplicate_item_id})`;
  const mirror = `and(primary_item_id.eq.${row.duplicate_item_id},duplicate_item_id.eq.${row.primary_item_id})`;
  const { data: existing, error: lookupError } = await client
    .from('item_cross_reference_suggestions')
    .select(SUGGESTION_COLUMNS)
    .eq('store_id', storeId)
    .or(`${pair},${mirror}`)
    .in('status', ['pending', 'dismissed'])
    .limit(2);
  throwIfError(lookupError);
  const existingRows = existing || [];
  // The query only matches pending/dismissed rows, so anything not marked
  // dismissed is a live pending suggestion to reuse.
  const pending = existingRows.find((existingRow) => existingRow.status !== 'dismissed');
  if (pending) return pending;
  const dismissed = existingRows.find((existingRow) => existingRow.status === 'dismissed');
  if (dismissed && !overrideDismissed) return null;

  const { data, error } = await client
    .from('item_cross_reference_suggestions')
    .insert(row)
    .select()
    .single();
  if (error) {
    if (error.code === '23505' || /duplicate key/i.test(error.message || '')) return null;
    throw new Error(error.message);
  }
  return data;
}

/**
 * Runs the cross-reference matcher for a freshly entered manual item and, when
 * it finds a candidate, records a pending suggestion. Manual entry is
 * explicit: even a confident 'attach' verdict never auto-moves anything — it
 * is recorded as a suggestion with an 'auto:'-prefixed reason so a human
 * confirms. Best-effort: any failure here is console.warn + null, never a
 * thrown error (the purchase option is already written by this point).
 */
async function maybeRecordSuggestion(storeId, item, opts, client) {
  if (!Array.isArray(opts.catalog)) return null;
  try {
    const match = findCrossReference(opts.catalog, {
      name: item.description,
      vendor: item.vendor,
      unit: item.unit,
      packSize: item.pack_size,
      brand: item.brand,
    });
    if (match.decision === 'none' || !match.itemId) return null;

    const reason = match.decision === 'attach' ? `auto:${match.reason}` : match.reason;
    return await insertSuggestion(
      storeId,
      {
        store_id: storeId,
        primary_item_id: match.itemId,
        duplicate_item_id: item.id,
        score: match.score,
        reason,
        channel: opts.channel ?? 'manual',
        status: 'pending',
      },
      client,
    );
  } catch (err) {
    console.warn('[item-cross-reference] Recording suggestion failed:', err?.message);
    return null;
  }
}

/**
 * Mirrors a MANUALLY entered inventory item (items page, add-while-counting)
 * into a purchase option — the item's own way-to-buy — via
 * upsertManualOption. A vendor, package, or product code is enough to preserve
 * a still-unpriced way to buy; only a row with no price AND no purchase
 * identity is skipped. When opts.catalog (the store's other items) is
 * provided, also runs the cross-reference matcher and records a pending
 * suggestion for 'suggest'/'attach' verdicts (never auto-merges).
 *
 * @param {number|string} storeId
 * @param {Object} item - the inventory_items row (id, description, vendor,
 *                        vendor_id, unit, pack_size, brand, case_price,
 *                        case_quantity, unit_price)
 * @param {Object} [opts] - { catalog: Array, channel: string }
 * @returns {Promise<{ option: Object, suggestion: Object|null }|null>}
 */
export async function createOptionForItem(storeId, item, opts = {}, client = sharedSupabase) {
  if (!storeId) throw new Error('storeId is required');
  if (!item?.id) throw new Error('item.id is required');

  const casePrice = positiveNumber(item.case_price);
  const unitPrice = positiveNumber(item.unit_price);
  const packagePrice = casePrice ?? unitPrice;
  const productCode = String(item.manufacturer_code ?? item.supc ?? '').trim();
  const hasPurchaseIdentity = Boolean(
    item.vendor_id
      || String(item.vendor ?? '').trim()
      || String(item.pack_size ?? '').trim()
      || productCode,
  );
  if (packagePrice === null && !hasPurchaseIdentity) return null;

  const caseQuantity = Number(item.case_quantity);
  // A positive unit price remains a price for the item's count unit even when
  // descriptive pack text is present ("750 mL", unit=bottle). Pack text only
  // supplies the orderable-package identity when the row is genuinely
  // unpriced; it must not reinterpret an existing unit-price contract.
  const buyingByTheCase = casePrice !== null
    || (packagePrice === null && Boolean(String(item.pack_size ?? '').trim()));
  const statedCaseQty = Number.isFinite(caseQuantity) && caseQuantity > 0 ? caseQuantity : null;

  // How many count units are in one package. Buying by the unit answers itself:
  // one gallon per gallon. Buying by the CASE does not — a case with no stated
  // quantity holds an unknown amount, and writing 1 there would say "one gallon
  // per case", which prices the case as a gallon and orders one case where
  // twelve were needed. Unresolved is the honest answer; the package shows
  // under "Needs unit review" until somebody confirms its size.
  const fields = {
    purchase_unit: buyingByTheCase
      ? (statedOrderUnitFromPack(item.pack_size) || 'case')
      : item.unit ?? null,
    qty_per_package: buyingByTheCase ? statedCaseQty : 1,
    package_price: packagePrice,
    conversion_status: buyingByTheCase && statedCaseQty === null ? 'unresolved' : 'valid',
  };
  if (item.vendor_id) fields.vendor_id = item.vendor_id;
  if (item.vendor) fields.vendor_name_text = item.vendor;
  if (productCode) fields.vendor_sku = productCode;
  if (item.description) fields.vendor_description = item.description;
  if (item.pack_size) fields.pack_description = item.pack_size;

  // The recency stamp names the EVENT: an item created while counting and one
  // typed on the items page send a disagreeing user to different places.
  const operationalSource =
    opts.channel === 'counting' ? 'count_item_creation' : 'manual_item_creation';
  const option = await upsertManualOption(storeId, item.id, fields, { operationalSource }, client);
  const suggestion = await maybeRecordSuggestion(storeId, item, opts, client);
  return { option, suggestion };
}

/**
 * Pending cross-reference suggestions for a store, newest first, with the
 * primary/duplicate item descriptions merged in (two-step fetch: suggestions,
 * then an .in() lookup on inventory_items). Suggestions referencing a
 * soft-deleted (or vanished) item are filtered out — the resolve RPC would
 * refuse to merge them anyway, so listing them only offers dead ends.
 */
export async function fetchSuggestions(storeId, client = sharedSupabase) {
  if (!storeId) return [];
  const { data: suggestions, error } = await client
    .from('item_cross_reference_suggestions')
    .select(SUGGESTION_COLUMNS)
    .eq('store_id', storeId)
    .eq('status', 'pending')
    .order('created_at', { ascending: false });
  if (error) {
    if (isMissingSchemaError(error)) {
      warnMissingSchema('item_cross_reference_suggestions', error);
      return [];
    }
    throw new Error(error.message);
  }

  const rows = suggestions || [];
  const itemIds = [...new Set(rows.flatMap((row) => [row.primary_item_id, row.duplicate_item_id]))]
    .filter((id) => id !== null && id !== undefined);
  if (!itemIds.length) return rows;

  const { data: items, error: itemsError } = await client
    .from('inventory_items')
    .select('id, description, is_active, deleted_at')
    .in('id', itemIds);
  throwIfError(itemsError);

  const itemById = new Map((items || []).map((row) => [String(row.id), row]));
  // One liveness rule, shared with counts and the SQL predicate
  // inventory_item_is_live. This used to test is_active alone, so an item
  // stamped deleted_at without is_active flipping still propped up a
  // suggestion — and the header badge counted it.
  //
  // Deliberately UNLIKE inventory_item_is_live in one respect: a row that
  // could not be loaded counts as dead here. Preserve-unknown is right for an
  // orderable count line (dropping it loses real work); for a merge suggestion
  // a vanished item is a dead end and offering it is the bug.
  const liveItem = (id) => {
    const item = itemById.get(String(id));
    return item && isLiveInventoryItem(item) ? item : null;
  };
  return rows
    .filter((row) => liveItem(row.primary_item_id) && liveItem(row.duplicate_item_id))
    .map((row) => ({
      ...row,
      primary_description: liveItem(row.primary_item_id)?.description ?? null,
      duplicate_description: liveItem(row.duplicate_item_id)?.description ?? null,
    }));
}

/**
 * How many pending suggestions are waiting, so the catalog can say so on the
 * collapsed panel instead of hiding them. Counts the same live-item-only set
 * fetchSuggestions lists, and returns 0 rather than throwing on any failure —
 * a badge is never worth breaking the catalog over.
 */
export async function countPendingSuggestions(storeId, client = sharedSupabase) {
  if (!storeId) return 0;
  try {
    return (await fetchSuggestions(storeId, client)).length;
  } catch (error) {
    console.warn('[item-cross-reference] pending suggestion count unavailable:', error?.message);
    return 0;
  }
}

async function resolveSuggestion(id, action, client) {
  if (!id) throw new Error('suggestion id is required');
  const { data, error } = await client.rpc('resolve_cross_reference_suggestion', {
    p_suggestion_id: id,
    p_action: action,
    p_actor: null,
  });
  if (error) {
    if (isMissingSchemaError(error)) {
      warnMissingSchema('resolve_cross_reference_suggestion', error);
      return null;
    }
    throw new Error(error.message);
  }
  return parseJsonb(data);
}

/**
 * Accepts a suggestion by recording a confirmed same_item RELATIONSHIP.
 * Both items survive; no options move and nothing is archived — "these are
 * the same product" is a link, not a merge. (The old accept called
 * resolve_cross_reference_suggestion, which moved every option onto the
 * primary and archived the duplicate.) Returns parsed jsonb
 * ({ linked, relationship_id } or { dismissed_inactive: true }), or null when
 * the migration is not deployed.
 */
export async function acceptSuggestion(id, client = sharedSupabase) {
  if (!id) throw new Error('suggestion id is required');
  const { data, error } = await client.rpc('accept_suggestion_as_relationship', {
    p_suggestion_id: id,
  });
  if (error) {
    if (isMissingSchemaError(error)) {
      warnMissingSchema('accept_suggestion_as_relationship', error);
      return null;
    }
    throw new Error(error.message);
  }
  const result = parseJsonb(data);
  if (result) void recomputeAfterRelationshipChange(client, { suggestionId: id });
  return result;
}

/** Dismisses a suggestion (keeps both items; marks the row dismissed). */
export async function dismissSuggestion(id, client = sharedSupabase) {
  return resolveSuggestion(id, 'dismiss', client);
}

/**
 * Re-points one purchase option at a different item (editor "this is the same
 * product" action). meta: { channel = 'editor', score, reason }. Returns the
 * updated option (parsed jsonb), or null when the migration is not deployed.
 */
export async function attachOptionToItem(optionId, targetItemId, meta = {}, client = sharedSupabase) {
  if (!optionId) throw new Error('optionId is required');
  if (!targetItemId) throw new Error('targetItemId is required');
  const { data, error } = await client.rpc('attach_purchase_option_to_item', {
    p_option_id: optionId,
    p_target_item_id: targetItemId,
    p_channel: meta.channel ?? 'editor',
    p_score: meta.score ?? null,
    p_reason: meta.reason ?? null,
  });
  if (error) {
    if (isMissingSchemaError(error)) {
      warnMissingSchema('attach_purchase_option_to_item', error);
      return null;
    }
    throw new Error(error.message);
  }
  const attached = parseJsonb(data);
  if (attached) {
    void recomputeAfterRelationshipChange(client, {
      storeId: attached.store_id ?? null,
      itemIds: [targetItemId],
    });
  }
  return attached;
}

/**
 * Splits an option off its item into a brand-new inventory item (undo a wrong
 * attach). Returns parsed jsonb ({ new_item_id, option }), or null when the
 * migration is not deployed.
 */
export async function detachOptionToNewItem(optionId, client = sharedSupabase) {
  if (!optionId) throw new Error('optionId is required');
  const { data, error } = await client.rpc('detach_purchase_option_to_new_item', {
    p_option_id: optionId,
  });
  if (error) {
    if (isMissingSchemaError(error)) {
      warnMissingSchema('detach_purchase_option_to_new_item', error);
      return null;
    }
    throw new Error(error.message);
  }
  const detached = parseJsonb(data);
  if (detached) {
    void recomputeAfterRelationshipChange(client, {
      storeId: detached.option?.store_id ?? null,
      itemIds: [detached.new_item_id].filter((id) => id != null),
    });
  }
  return detached;
}

/**
 * Combines two canonical items: `duplicateItemId`'s purchase options move onto
 * `primaryItemId`, and the duplicate is archived. Used by the item page's
 * connect/disconnect editor, where a human has already decided the two rows are
 * the same product.
 *
 * Implemented as record-then-accept against the same suggestion RPC the review
 * queue uses, so the move lands in item_cross_reference_events with a
 * reversible 'suggestion_accepted' entry — a merge made here is as undoable as
 * one made from the queue. PARs, count templates, and recipes are preserved by
 * the RPC (see 20260724140003_merge_preserves_and_undo.sql).
 *
 * @param {number|string} storeId
 * @param {number|string} duplicateItemId - the item that will be archived
 * @param {number|string} primaryItemId   - the item that survives
 * @param {Object} [meta] - { channel = 'editor', score, reason }
 * @returns {Promise<Object|null>} the RPC result, or null when the migration
 *                                 is not deployed.
 */
export async function combineItems(storeId, duplicateItemId, primaryItemId, meta = {}, client = sharedSupabase) {
  if (!storeId) throw new Error('storeId is required');
  if (!duplicateItemId) throw new Error('duplicateItemId is required');
  if (!primaryItemId) throw new Error('primaryItemId is required');
  if (String(duplicateItemId) === String(primaryItemId)) {
    throw new Error('An item cannot be combined with itself.');
  }

  let suggestion;
  try {
    suggestion = await insertSuggestion(
      storeId,
      {
        store_id: storeId,
        primary_item_id: primaryItemId,
        duplicate_item_id: duplicateItemId,
        score: meta.score ?? null,
        reason: meta.reason ?? 'manual:item-editor',
        channel: meta.channel ?? 'editor',
        status: 'pending',
      },
      client,
      // The editor combine is an explicit human decision — it may reverse an
      // earlier dismissal, where an automatic matcher suggestion may not.
      { overrideDismissed: true },
    );
  } catch (error) {
    if (isMissingSchemaError(error)) {
      warnMissingSchema('item_cross_reference_suggestions', error);
      return null;
    }
    throw error;
  }

  // A concurrent writer already holding the unique index returns null from
  // insertSuggestion; re-read the pending row so the accept still has a target.
  let suggestionId = suggestion?.id ?? null;
  if (!suggestionId) {
    const { data } = await client
      .from('item_cross_reference_suggestions')
      .select('id')
      .eq('store_id', storeId)
      .eq('primary_item_id', primaryItemId)
      .eq('duplicate_item_id', duplicateItemId)
      .eq('status', 'pending')
      .limit(1);
    suggestionId = data && data.length ? data[0].id : null;
  }
  if (!suggestionId) throw new Error('Could not record the item combination.');

  return acceptSuggestion(suggestionId, client);
}

/**
 * Append-only audit trail from item_cross_reference_events, newest first,
 * capped at 100 rows, optionally scoped to one inventory item.
 */
export async function fetchEventLog(storeId, { inventoryItemId } = {}, client = sharedSupabase) {
  if (!storeId) return [];
  let query = client
    .from('item_cross_reference_events')
    .select('*')
    .eq('store_id', storeId);
  if (inventoryItemId) query = query.eq('inventory_item_id', inventoryItemId);
  const { data, error } = await query
    .order('created_at', { ascending: false })
    .limit(100);
  if (error) {
    if (isMissingSchemaError(error)) {
      warnMissingSchema('item_cross_reference_events', error);
      return [];
    }
    throw new Error(error.message);
  }
  return data || [];
}
